/**
 * Leads Service
 * Client para API de Leads
 */

import type {
  Lead,
  LeadListResponse,
  LeadStats,
  CreateLeadRequest,
  UpdateLeadRequest,
  UpdateStageRequest,
  MarkAsLostRequest,
  CreateInteractionRequest,
  ConversionResult,
  ConvertToClientRequest,
  LeadFilters,
  LeadPaginationOptions,
  LeadInteraction
} from '../types/leads';

class LeadsService {
  private baseUrl = '/api/leads';

  /**
   * Helper para obter headers com autenticação
   */
  private getHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * Helper para construir query string
   */
  private buildQueryString(filters: LeadFilters, pagination?: LeadPaginationOptions): string {
    const params = new URLSearchParams();

    // Filtros
    if (filters.search) params.append('search', filters.search);
    if (filters.stage) params.append('stage', filters.stage);
    if (filters.source) params.append('source', filters.source);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters.unassigned) params.append('unassigned', 'true');
    if (filters.isHotLead) params.append('isHotLead', 'true');
    if (filters.isQualified) params.append('isQualified', 'true');
    if (filters.minScore !== undefined) params.append('minScore', filters.minScore.toString());
    if (filters.maxScore !== undefined) params.append('maxScore', filters.maxScore.toString());
    if (filters.hasOverdueFollowUp) params.append('hasOverdueFollowUp', 'true');
    if (filters.hasFollowUpToday) params.append('hasFollowUpToday', 'true');
    if (filters.legalArea) params.append('legalArea', filters.legalArea);
    if (filters.minEstimatedValue !== undefined) params.append('minEstimatedValue', filters.minEstimatedValue.toString());
    if (filters.maxEstimatedValue !== undefined) params.append('maxEstimatedValue', filters.maxEstimatedValue.toString());
    if (filters.createdFrom) params.append('createdFrom', filters.createdFrom);
    if (filters.createdTo) params.append('createdTo', filters.createdTo);
    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(tag => params.append('tags', tag));
    }

    // Paginação
    if (pagination) {
      if (pagination.limit) params.append('limit', pagination.limit.toString());
      if (pagination.offset) params.append('offset', pagination.offset.toString());
      if (pagination.sortBy) params.append('sortBy', pagination.sortBy);
      if (pagination.sortOrder) params.append('sortOrder', pagination.sortOrder);
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * GET /api/leads
   * Listar leads
   */
  async list(
    filters: LeadFilters = {},
    pagination?: LeadPaginationOptions
  ): Promise<LeadListResponse> {
    const queryString = this.buildQueryString(filters, pagination);
    const response = await fetch(`${this.baseUrl}${queryString}`, {
      method: 'GET',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');

      // Se retornou HTML, significa que há um erro de roteamento ou configuração
      if (contentType?.includes('text/html')) {
        throw new Error('Erro de configuração do servidor. Contate o administrador.');
      }

      // Tentar parsear JSON de erro
      try {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Erro ao listar leads');
      } catch (e) {
        // Se não conseguir parsear JSON, usar mensagem genérica
        throw new Error('Erro ao listar leads');
      }
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * GET /api/leads/:id
   * Buscar lead por ID
   */
  async getById(id: string): Promise<Lead> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'GET',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Lead não encontrado');
      }
      throw new Error('Erro ao buscar lead');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * POST /api/leads
   * Criar novo lead
   */
  async create(data: CreateLeadRequest): Promise<Lead> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');

      // Se retornou HTML, significa que há um erro de roteamento ou configuração
      if (contentType?.includes('text/html')) {
        throw new Error('Erro de configuração do servidor. Contate o administrador.');
      }

      // Tentar parsear JSON de erro
      try {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Erro ao criar lead');
      } catch (e) {
        // Se não conseguir parsear JSON, usar mensagem genérica
        if (e instanceof Error && e.message.includes('Erro')) {
          throw e; // Re-throw se já é uma mensagem de erro customizada
        }
        throw new Error('Erro ao criar lead');
      }
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * PATCH /api/leads/:id
   * Atualizar lead
   */
  async update(id: string, data: UpdateLeadRequest): Promise<Lead> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao atualizar lead');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * PATCH /api/leads/:id/stage
   * Atualizar estágio do lead
   */
  async updateStage(id: string, data: UpdateStageRequest): Promise<Lead> {
    const response = await fetch(`${this.baseUrl}/${id}/stage`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao atualizar estágio');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * POST /api/leads/:id/mark-as-lost
   * Marcar lead como perdido
   */
  async markAsLost(id: string, data: MarkAsLostRequest): Promise<Lead> {
    const response = await fetch(`${this.baseUrl}/${id}/mark-as-lost`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao marcar lead como perdido');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * DELETE /api/leads/:id
   * Deletar lead
   */
  async delete(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao deletar lead');
    }
  }

  /**
   * POST /api/leads/:id/interactions
   * Adicionar interação ao lead
   */
  async addInteraction(id: string, data: CreateInteractionRequest): Promise<LeadInteraction> {
    const response = await fetch(`${this.baseUrl}/${id}/interactions`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao adicionar interação');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * POST /api/leads/:id/convert
   * Converter lead em cliente
   */
  async convertToClient(id: string, data: ConvertToClientRequest): Promise<ConversionResult> {
    const response = await fetch(`${this.baseUrl}/${id}/convert`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao converter lead');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * POST /api/leads/:id/recalculate-score
   * Recalcular score do lead
   */
  async recalculateScore(id: string): Promise<Lead> {
    const response = await fetch(`${this.baseUrl}/${id}/recalculate-score`, {
      method: 'POST',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erro ao recalcular score');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * GET /api/leads/stats
   * Obter estatísticas (FASE 2 - requer feature flag)
   */
  async getStats(): Promise<LeadStats> {
    const response = await fetch(`${this.baseUrl}/stats`, {
      method: 'GET',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Estatísticas não disponíveis (feature desabilitada)');
      }
      throw new Error('Erro ao obter estatísticas');
    }

    const result = await response.json();
    return result.data;
  }
}

export const leadsService = new LeadsService();
export default leadsService;
